(() => {
  if (window.DmWishlistController?.destroy) {
    window.DmWishlistController.destroy();
  }

  const storageKey = 'dearmora:wishlist:v1';
  const ajaxSectionId = 'wishlist-card-ajax';
  const selectors = {
    toggle: '[data-wishlist-toggle]',
    count: '[data-wishlist-count]',
    link: '[data-wishlist-link]',
    clear: '[data-wishlist-clear]',
    page: '[data-wishlist-page]',
    pageEmpty: '[data-wishlist-page-empty]',
    pageContent: '[data-wishlist-page-content]',
    pageItems: '[data-wishlist-page-items]',
    pageLoading: '[data-wishlist-page-loading]',
    pageStatus: '[data-wishlist-page-status]',
  };

  let wishlistItems = readWishlist();
  let mediaSyncFrame = null;
  let mediaObserver = null;
  let pageAbortController = null;
  let pageRenderId = 0;
  let lastToggleId = '';
  let lastToggleAt = 0;

  function readWishlist() {
    try {
      const value = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(value)) return [];

      const seen = new Set();
      return value.reduce((items, item) => {
        const id = String(item?.id || '');
        if (!id || seen.has(id)) return items;

        const url = String(item?.url || '');
        const storedHandle = String(item?.handle || '');
        const urlHandle = url.includes('/products/') ? url.split('/products/')[1].split(/[?#]/)[0] : '';
        const handle = storedHandle || urlHandle;
        if (!handle) return items;

        seen.add(id);
        items.push({
          id,
          handle,
          title: String(item?.title || ''),
          url: url || `/products/${handle}`,
        });
        return items;
      }, []);
    } catch (_error) {
      return [];
    }
  }

  function saveWishlist(message) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(wishlistItems));
    } catch (_error) {
      // Keep the current in-memory wishlist if storage is unavailable.
    }

    syncInterface(message);
    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        detail: { items: wishlistItems.slice() },
      })
    );
  }

  function itemFromButton(button) {
    return {
      id: String(button.dataset.productId || ''),
      handle: button.dataset.productHandle || '',
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || '',
    };
  }

  function containsProduct(productId) {
    return wishlistItems.some((item) => String(item.id) === String(productId));
  }

  function toggleProduct(button) {
    const item = itemFromButton(button);
    if (!item.id || !item.handle) return;

    if (containsProduct(item.id)) {
      wishlistItems = wishlistItems.filter((wishlistItem) => String(wishlistItem.id) !== item.id);
      saveWishlist(`${item.title} removed from your wishlist.`);
      return;
    }

    wishlistItems = [item, ...wishlistItems];
    saveWishlist(`${item.title} added to your wishlist.`);
  }

  function removeProduct(productId) {
    const removedItem = wishlistItems.find((item) => String(item.id) === String(productId));
    wishlistItems = wishlistItems.filter((item) => String(item.id) !== String(productId));
    saveWishlist(removedItem ? `${removedItem.title} removed from your wishlist.` : 'Product removed from your wishlist.');
  }

  function clearWishlist() {
    wishlistItems = [];
    saveWishlist('Your wishlist has been cleared.');
  }

  function updateToggles() {
    document.querySelectorAll(selectors.toggle).forEach((button) => {
      const active = containsProduct(button.dataset.productId);
      const title = button.dataset.productTitle || 'this product';
      const action = active ? 'Remove' : 'Add';
      const label = active ? 'Remove from wishlist' : 'Add to wishlist';

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${action} ${title} ${active ? 'from' : 'to'} wishlist`);
      button.setAttribute('title', label);

      const labelElement = button.querySelector('[data-wishlist-toggle-label]');
      if (labelElement) labelElement.textContent = label;
    });
  }

  function updateHeader() {
    const itemCount = wishlistItems.length;

    document.querySelectorAll(selectors.count).forEach((count) => {
      count.textContent = itemCount > 99 ? '99+' : String(itemCount);
      count.hidden = itemCount === 0;
    });

    document.querySelectorAll(selectors.link).forEach((link) => {
      link.setAttribute('aria-label', `View wishlist, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`);
    });
  }

  function normalizeRootUrl(rootUrl) {
    const value = rootUrl || '/';
    return value.endsWith('/') ? value : `${value}/`;
  }

  function getCardRequestUrl(item) {
    const rootUrl = normalizeRootUrl(window.Shopify?.routes?.root || '/');
    return `${rootUrl}products/${encodeURIComponent(item.handle)}?section_id=${ajaxSectionId}`;
  }

  async function fetchWishlistCard(item, signal) {
    const response = await fetch(getCardRequestUrl(item), {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`Wishlist card request failed: ${response.status}`);
    }

    const html = await response.text();
    const responseDocument = new DOMParser().parseFromString(html, 'text/html');
    return responseDocument.querySelector('[data-wishlist-ajax-card]');
  }

  async function renderWishlistPage(message) {
    const page = document.querySelector(selectors.page);
    if (!page) return;

    const empty = page.querySelector(selectors.pageEmpty);
    const content = page.querySelector(selectors.pageContent);
    const itemsContainer = page.querySelector(selectors.pageItems);
    const loading = page.querySelector(selectors.pageLoading);
    const status = page.querySelector(selectors.pageStatus);
    const hasItems = wishlistItems.length > 0;

    if (status && message) status.textContent = message;

    pageAbortController?.abort();
    pageAbortController = null;
    pageRenderId += 1;

    if (!hasItems) {
      if (empty) empty.hidden = false;
      if (content) content.hidden = true;
      if (loading) loading.hidden = true;
      if (itemsContainer) {
        itemsContainer.hidden = false;
        itemsContainer.removeAttribute('aria-busy');
        itemsContainer.replaceChildren();
      }
      return;
    }

    if (empty) empty.hidden = true;
    if (content) content.hidden = false;
    if (loading) loading.hidden = false;
    if (!itemsContainer) return;

    itemsContainer.hidden = true;
    itemsContainer.setAttribute('aria-busy', 'true');

    const currentRenderId = pageRenderId;
    const itemsSnapshot = wishlistItems.slice();
    pageAbortController = new AbortController();

    try {
      const cards = await Promise.all(
        itemsSnapshot.map((item) =>
          fetchWishlistCard(item, pageAbortController.signal).catch((error) => {
            if (error.name === 'AbortError') throw error;
            return null;
          })
        )
      );

      if (currentRenderId !== pageRenderId) return;

      const fragment = document.createDocumentFragment();
      cards.filter(Boolean).forEach((card) => fragment.appendChild(card));
      itemsContainer.replaceChildren(fragment);
      itemsContainer.hidden = false;
      itemsContainer.setAttribute('aria-busy', 'false');
      if (loading) loading.hidden = true;

      document.dispatchEvent(
        new CustomEvent('wishlist:rendered', {
          detail: { itemCount: itemsContainer.children.length },
        })
      );
    } catch (error) {
      if (error.name === 'AbortError' || currentRenderId !== pageRenderId) return;

      itemsContainer.hidden = false;
      itemsContainer.setAttribute('aria-busy', 'false');
      if (loading) loading.hidden = true;
      if (status) status.textContent = 'Your wishlist could not be loaded. Please refresh and try again.';
    }
  }

  function syncInterface(message) {
    ensureMediaButtons();
    updateToggles();
    updateHeader();
    renderWishlistPage(message);
  }

  function ensureMediaButtons() {
    const sourceButton = document.querySelector('.dm-wishlist-toggle--product-media');
    if (!sourceButton) return;

    document.querySelectorAll('.product__media-wrapper').forEach((mediaWrapper) => {
      const directButton = Array.from(mediaWrapper.children).find((child) =>
        child.classList?.contains('dm-wishlist-toggle--product-media')
      );

      if (!directButton) {
        mediaWrapper.appendChild(sourceButton.cloneNode(true));
      }
    });
  }

  function scheduleMediaSync() {
    if (mediaSyncFrame !== null) return;

    mediaSyncFrame = window.requestAnimationFrame(() => {
      mediaSyncFrame = null;
      ensureMediaButtons();
      updateToggles();
    });
  }

  function handleClick(event) {
    const toggle = event.target.closest(selectors.toggle);
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();

      const productId = String(toggle.dataset.productId || '');
      const now = Date.now();
      if (productId && productId === lastToggleId && now - lastToggleAt < 350) return;

      lastToggleId = productId;
      lastToggleAt = now;
      toggleProduct(toggle);
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      removeProduct(removeButton.dataset.wishlistRemove);
      return;
    }

    if (event.target.closest(selectors.clear)) {
      event.preventDefault();
      clearWishlist();
    }
  }

  function handleSectionLoad() {
    syncInterface();
  }

  function handleStorage(event) {
    if (event.key !== storageKey) return;
    wishlistItems = readWishlist();
    syncInterface();
  }

  function observeMediaChanges() {
    if (!document.body || !window.MutationObserver) return;

    mediaObserver?.disconnect();
    mediaObserver = new MutationObserver((mutations) => {
      const mediaChanged = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node instanceof Element &&
            (node.matches('.product__media-wrapper, .rio-media-gallery, #pwzrswiper-container-wrapper') ||
              node.querySelector('.product__media-wrapper, .rio-media-gallery, #pwzrswiper-container-wrapper'))
        )
      );

      if (mediaChanged) scheduleMediaSync();
    });

    mediaObserver.observe(document.body, { childList: true, subtree: true });
  }

  function initialize() {
    syncInterface();
    observeMediaChanges();
  }

  function destroy() {
    document.removeEventListener('click', handleClick);
    document.removeEventListener('shopify:section:load', handleSectionLoad);
    document.removeEventListener('DOMContentLoaded', initialize);
    window.removeEventListener('storage', handleStorage);

    pageAbortController?.abort();
    pageAbortController = null;
    mediaObserver?.disconnect();
    mediaObserver = null;

    if (mediaSyncFrame !== null) {
      window.cancelAnimationFrame(mediaSyncFrame);
      mediaSyncFrame = null;
    }
  }

  window.DmWishlistController = {
    destroy,
    refresh: syncInterface,
  };

  document.addEventListener('click', handleClick);
  document.addEventListener('shopify:section:load', handleSectionLoad);
  window.addEventListener('storage', handleStorage);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
